import React, { useState, useEffect } from 'react';
import { Clock, X, Trash2, Plus } from 'lucide-react';

const TargetHoursModal = ({ show, onClose, employee, onSave, t, isRTL, formatHours }) => {
  const [history, setHistory] = useState([]);
  const [newMonth, setNewMonth] = useState(new Date().toISOString().substring(0, 7));
  const [newHours, setNewHours] = useState('');

  useEffect(() => {
    if (show && employee) {
      setHistory(employee.targetHoursHistory || []);
    }
  }, [show, employee]);

  if (!show || !employee) return null;

  const handleAddRule = () => {
    if (!newMonth || !newHours || isNaN(parseFloat(newHours))) return;
    const rule = { monthKey: newMonth, hours: parseFloat(newHours) };

    // Check if rule for this month already exists, update or add it
    const updatedHistory = [...history];
    const existingIdx = updatedHistory.findIndex(h => h.monthKey === newMonth);
    if (existingIdx >= 0) {
      updatedHistory[existingIdx] = rule;
    } else {
      updatedHistory.push(rule);
    }

    // Sort descending by monthKey
    updatedHistory.sort((a, b) => b.monthKey.localeCompare(a.monthKey));
    setHistory(updatedHistory);
    setNewHours('');
  };

  const handleDeleteRule = (monthKey) => {
    setHistory(history.filter(h => h.monthKey !== monthKey));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
            <span className="bg-blue-100 text-accent-mint p-2 rounded-lg mr-3"><Clock className="w-5 h-5" /></span>
            Historie: Soll-Stunden
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-slate-300">
            Mitarbeiter: <strong>{employee.name}</strong>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Standard (Fallback): {formatHours(employee.maxHours || 30)} Std
          </p>
        </div>

        <div className="space-y-4 max-h-[40vh] overflow-y-auto mb-4 border dark:border-zinc-700 rounded p-2">
          {history.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-slate-400 text-sm py-4">Bisher keine Einträge. Standard wird immer genutzt.</div>
          ) : (
            history.map((h, i) => (
              <div key={i} className="flex justify-between items-center bg-gray-50 dark:bg-zinc-700/50 p-2 rounded">
                <div>
                  <div className="font-bold text-sm dark:text-white">Ab {h.monthKey}</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="font-mono text-sm dark:text-gray-300">{formatHours(h.hours)} Std</div>
                  <button onClick={() => handleDeleteRule(h.monthKey)} className="text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 p-1 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-2 mb-6">
          <input
            type="month"
            value={newMonth}
            onChange={e => setNewMonth(e.target.value)}
            className="border p-2 rounded text-sm w-32 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
          />
          <input
            type="number"
            value={newHours}
            onChange={e => setNewHours(e.target.value)}
            placeholder="Std"
            className="border p-2 rounded text-sm flex-1 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
          />
          <button onClick={handleAddRule} className="bg-accent-mint text-black p-2 rounded hover:bg-emerald-400 flex justify-center items-center">
            <Plus className="w-5 h-5" />
          </button>
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-700 dark:text-slate-300 dark:hover:bg-zinc-600 rounded font-medium">
            Abbrechen
          </button>
          <button onClick={() => { onSave(employee.id, history); onClose(); }} className="px-4 py-2 bg-accent-mint text-black rounded hover:bg-emerald-400 font-medium tracking-wide">
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

export default TargetHoursModal;
